import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { PopupHeader, Spinner } from '@streamr/tv-ui';
import { executeSoon } from '@streamr/tv-utils';
import { format } from 'date-fns';
import { useEffect } from 'react';
import styled from 'styled-components';
import { RecordingPlaybackService } from '../../../services/recording/RecordingPlaybackService';
import { Popup } from '../../../utils/popups';
import { AbstractPopup, DismissalReason, } from '../AbstractPopup';
import { PopupCssPreset } from '../Popup.styles';
/**
 * Plays a recording. The player itself is a document served by the
 * recording service worker, shown in a frame: the worker answers the
 * requests of the player's <video> with the bytes of an MP4 that it puts
 * together from the encrypted recording as they are asked for.
 */
const RecordingPlayerPopup = ({ reject, recording, broadcast, indexKeyFromLink, ...props }) => {
    const playback = RecordingPlaybackService.getOutput().usePlayback();
    useEffect(() => {
        const service = RecordingPlaybackService.getNotificationInterface();
        service.notifyOpenRecording(recording, broadcast, indexKeyFromLink);
        return () => {
            service.notifyClosePlayback();
        };
    }, [recording, broadcast, indexKeyFromLink]);
    const current = playback?.recordingId === recording.recordingId ? playback : undefined;
    return (_jsx(RecordingPlayerPopupRoot, { reject: reject, ...props, children: _jsxs(Layout, { children: [_jsx(PopupHeader, { onCloseButtonClick: () => {
                        reject(DismissalReason.CloseButton);
                    }, children: format(new Date(recording.timestamp), 'MMM do yyyy, HH:mm') }), _jsxs(Stage, { "data-automation-id": "recording-player-stage", children: [current?.status === 'ready' && (_jsx(PlayerFrame
                        // a new element for every player: a player
                        // that replaces one (see the service) must
                        // not become a step in the frame's history
                        , { src: current.playerUrl, title: "Recording", allow: "fullscreen; picture-in-picture; autoplay", allowFullScreen: true, "data-automation-id": "recording-player" }, current.playerUrl)), (current === undefined ||
                            current.status === 'opening') && _jsx(StyledSpinner, {}), current?.status === 'error' && (_jsx(Message, { "data-automation-id": "recording-player-error", children: current.message }))] })] }) }));
};
const RecordingPlayerPopupRoot = styled(AbstractPopup) `
    ${PopupCssPreset.Fullscreen};
    --Popup_Padding: 0;

    @media (min-width: 48rem) {
        ${PopupCssPreset.Center};
        --Popup_Width: min(72rem, calc(var(--vw, 1vw) * 90));
        --Popup_Height: min(48rem, calc(var(--vh, 1vh) * 90));
        --Popup_Padding: 0;
    }
`;
const Layout = styled.div `
    display: grid;
    grid-template-rows: max-content minmax(0, 1fr);
    height: 100%;
    padding: 1rem;
    gap: 0.75rem;
    box-sizing: border-box;
`;
const Stage = styled.div `
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 0;
    border-radius: var(--tv-ui-border-radius-2);
    overflow: hidden;
    background: #000000;
`;
const PlayerFrame = styled.iframe `
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
`;
const Message = styled.div `
    padding: 2rem;
    text-align: center;
    color: #ffffff;
`;
const StyledSpinner = styled(Spinner) `
    --Spinner_Size: 3rem;
    --Spinner_TrackColor: var(--tv-ui-color-2);
    --Spinner_Color: var(--tv-ui-color-3);
    --Spinner_Thickness: 3px;
`;
const recordingPlayerPopup = Popup.create(RecordingPlayerPopup);
export function showRecordingPlayerPopup({ recording, broadcast, indexKeyFromLink, onDismiss, }) {
    executeSoon(async () => {
        try {
            await recordingPlayerPopup.pop({
                recording,
                broadcast,
                indexKeyFromLink,
            });
        }
        catch (_) {
        }
        finally {
            onDismiss?.();
        }
    });
}
